import { useEffect } from 'react';
import { motion } from 'framer-motion';
import PlayingCard from 'components/PlayingCard';
import WinnerCrown from 'components/WinnerCrown';
import { useSound } from 'context/SoundProvider';
import { avatarColor, avatarInitial } from 'lib/avatar';
import { formatSeatPlayerName } from 'lib/currency-format';
import { displayHandCategory } from 'lib/hand-category';
import type { SideShowResultEvent } from 'types/game';

interface SideShowCompareOverlayProps {
  result: SideShowResultEvent;
  viewerUserId?: number;
}

const panelEase = [0.22, 1, 0.36, 1] as const;

function PlayerRevealRow({
  player,
  outcome,
  isMe,
  rowDelay,
}: {
  player: SideShowResultEvent['requester'];
  outcome: 'won' | 'lost';
  isMe: boolean;
  rowDelay: number;
}) {
  const displayName = formatSeatPlayerName(player.username, isMe);
  const isWinner = outcome === 'won';

  return (
    <motion.div
      className={`tp-sideshow-row tp-sideshow-row-${outcome}`}
      initial={{ opacity: 0, x: isWinner ? -28 : 28, scale: 0.96 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      transition={{ delay: rowDelay, duration: 0.48, ease: panelEase }}
    >
      {isWinner && (
        <>
          <div className="tp-sideshow-win-rays" aria-hidden />
          <div className="tp-sideshow-win-green-glow" aria-hidden />
          <div className="tp-sideshow-win-shimmer" aria-hidden />
          <div className="tp-sideshow-sparkles" aria-hidden>
            {['s1', 's2', 's3', 's4', 's5', 's6'].map((id) => (
              <span key={id} className={`tp-sideshow-spark tp-sideshow-spark-${id}`} />
            ))}
          </div>
          <div className="tp-sideshow-win-glow" aria-hidden />
        </>
      )}

      {!isWinner && (
        <>
          <div className="tp-sideshow-lost-glow" aria-hidden />
          <div className="tp-sideshow-lost-red-glow" aria-hidden />
          <div className="tp-sideshow-lost-shimmer" aria-hidden />
          <div className="tp-sideshow-lost-overlay" aria-hidden />
        </>
      )}

      <div className={`tp-sideshow-row-body${isWinner ? ' tp-sideshow-row-body-won' : ' tp-sideshow-row-body-lost'}`}>
        {isWinner ? (
          <div className="tp-sideshow-side tp-sideshow-side-winner">
            <div className="tp-sideshow-player-cluster">
              <div className="tp-sideshow-avatar-wrap tp-sideshow-avatar-wrap-winner">
                <div className="tp-sideshow-crown-wrap">
                  <WinnerCrown size={26} className="tp-sideshow-crown-icon" />
                </div>
                <motion.div
                  className="tp-sideshow-avatar tp-sideshow-avatar-winner"
                  style={{ background: avatarColor(player.username) }}
                  initial={{ scale: 0.85, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: rowDelay + 0.15, type: 'spring', stiffness: 340, damping: 18 }}
                >
                  {avatarInitial(player.username)}
                </motion.div>
              </div>
              <div className="tp-sideshow-name-block">
                <span className="tp-sideshow-name">{displayName}</span>
              </div>
            </div>

            <motion.span
              className="tp-sideshow-outcome-label tp-sideshow-outcome-label-won"
              initial={{ opacity: 0, scale: 0.7, x: 8 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              transition={{ delay: rowDelay + 0.28, type: 'spring', stiffness: 360, damping: 16 }}
            >
              Winner
            </motion.span>
          </div>
        ) : null}

        <motion.div
          className="tp-sideshow-cards-wrap"
          initial="hidden"
          animate="visible"
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.08, delayChildren: rowDelay + 0.22 } },
          }}
        >
          <div className="tp-sideshow-cards">
            {player.cards.map((card, index) => (
              <motion.div
                key={`${player.userId}-${card.code}-${index}`}
                className="tp-sideshow-card-slot"
                variants={{
                  hidden: { opacity: 0, y: 10, rotateY: -60, scale: 0.85 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    rotateY: 0,
                    scale: 1,
                    transition: { type: 'spring', stiffness: 340, damping: 22 },
                  },
                }}
              >
                <PlayingCard
                  code={card.code}
                  hidden={card.hidden}
                  small
                  compact
                  flatLayout
                  animate="none"
                />
              </motion.div>
            ))}
          </div>

          <motion.p
            className={`tp-sideshow-hand${isWinner ? '' : ' tp-sideshow-hand-lost'}`}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: rowDelay + 0.5, duration: 0.28 }}
          >
            {displayHandCategory(player.handCategory)}
          </motion.p>
        </motion.div>

        {!isWinner && (
          <div className="tp-sideshow-loser-group">
            <motion.span
              className="tp-sideshow-outcome-label tp-sideshow-outcome-label-lost"
              initial={{ opacity: 0, scale: 0.7, x: -12 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              transition={{ delay: rowDelay + 0.2, type: 'spring', stiffness: 360, damping: 18 }}
            >
              Lost
            </motion.span>

            <div className="tp-sideshow-player-cluster">
              <div className="tp-sideshow-avatar-wrap">
                <motion.div
                  className="tp-sideshow-avatar tp-sideshow-avatar-lost"
                  style={{ background: avatarColor(player.username) }}
                  initial={{ scale: 0.85, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: rowDelay + 0.15, type: 'spring', stiffness: 340, damping: 18 }}
                >
                  {avatarInitial(player.username)}
                </motion.div>
              </div>
              <div className="tp-sideshow-name-block">
                <span className="tp-sideshow-name">{displayName}</span>
                <span className="tp-sideshow-packed-tag">Packed</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}

/** Side-show compare overlay — horizontal banner layout (Octro-style) */
export default function SideShowCompareOverlay({
  result,
  viewerUserId,
}: SideShowCompareOverlayProps) {
  const { play } = useSound();
  const winner =
    result.winnerUserId === result.requester.userId ? result.requester : result.target;
  const loser =
    result.loserUserId === result.requester.userId ? result.requester : result.target;
  const viewerWon = viewerUserId != null && winner.userId === viewerUserId;

  useEffect(() => {
    play(viewerWon ? 'win' : 'showdown');
  }, [play, viewerWon]);

  return (
    <motion.div
      className="tp-sideshow-overlay pointer-events-none absolute inset-0 z-[70] flex items-center justify-center p-2 sm:p-3"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.28 }}
      role="status"
      aria-live="polite"
    >
      <motion.div
        className="tp-sideshow-overlay-backdrop absolute inset-0"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        aria-hidden
      />

      <motion.div
        className="tp-sideshow-panel relative z-[1] w-full max-w-[min(100%,26rem)]"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.94, y: 10 }}
        transition={{ duration: 0.42, ease: panelEase }}
      >
        <div className="tp-sideshow-panel-shine" aria-hidden />

        <PlayerRevealRow
          player={winner}
          outcome="won"
          isMe={viewerUserId != null && winner.userId === viewerUserId}
          rowDelay={0.1}
        />

        <motion.div
          className="tp-sideshow-vs-wrap"
          initial={{ opacity: 0, scale: 0.35, rotate: -12 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ delay: 0.32, type: 'spring', stiffness: 420, damping: 16 }}
          aria-hidden
        >
          <div className="tp-sideshow-vs-ring" />
          <motion.span
            className="tp-sideshow-vs"
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ delay: 0.5, duration: 0.55, ease: 'easeInOut', repeat: Infinity, repeatDelay: 1.2 }}
          >
            VS
          </motion.span>
        </motion.div>

        <PlayerRevealRow
          player={loser}
          outcome="lost"
          isMe={viewerUserId != null && loser.userId === viewerUserId}
          rowDelay={0.42}
        />
      </motion.div>
    </motion.div>
  );
}
